import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Key, Plus, Eye, EyeOff, Copy, Check } from 'lucide-react';
import { getApiKeys } from '../../../services/api';

const ApiKeysBlock = ({ user, size = 'medium', refreshTrigger }) => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleKeys, setVisibleKeys] = useState(new Set());
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    loadApiKeys();
  }, [user, refreshTrigger]);

  const loadApiKeys = async () => {
    if (!user?.organization_id) return;
    
    try {
      const keysData = await getApiKeys(user.organization_id);
      setApiKeys(keysData || []);
    } catch (error) {
      console.error('Failed to load API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDisplayCount = () => {
    switch (size) {
      case 'small':
        return 2;
      case 'medium':
        return 3;
      case 'large':
        return 6;
      default:
        return 3;
    }
  };

  const displayKeys = apiKeys.slice(0, getDisplayCount());

  const toggleKeyVisibility = (keyId) => {
    const newVisibleKeys = new Set(visibleKeys);
    if (newVisibleKeys.has(keyId)) {
      newVisibleKeys.delete(keyId);
    } else {
      newVisibleKeys.add(keyId);
    }
    setVisibleKeys(newVisibleKeys);
  };

  const copyToClipboard = async (text, keyId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(keyId);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (apiKeys.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <Key className="h-8 w-8 text-gray-400 dark:text-gray-500 mb-2" />
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">No API keys configured</p>
        <Link to="/api-keys" className="text-xs text-celadon-600 hover:text-celadon-700 dark:text-celadon-400 dark:hover:text-celadon-300">
          Add your first API key
        </Link>
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Key className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {apiKeys.length} API Key{apiKeys.length !== 1 ? 's' : ''}
          </span>
        </div>
        {apiKeys.length > getDisplayCount() && (
          <Link 
            to="/api-keys"
            className="text-xs text-celadon-600 hover:text-celadon-700 dark:text-celadon-400 dark:hover:text-celadon-300"
          >
            View all
          </Link>
        )}
      </div>

      <div className="space-y-2 overflow-y-auto max-h-full">
        {displayKeys.map((apiKey) => (
          <div
            key={apiKey.id}
            className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {apiKey.name}
                  </h4>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    apiKey.status === 'active' 
                      ? 'bg-celadon-100 text-celadon-800 dark:bg-celadon-900/30 dark:text-celadon-300' 
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                  }`}>
                    {apiKey.status}
                  </span>
                </div>
                
                {size !== 'small' && (
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    {apiKey.service} • Created {new Date(apiKey.created_at).toLocaleDateString()}
                  </p>
                )}

                <div className="flex items-center gap-2">
                  <code className="text-xs bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded font-mono text-gray-900 dark:text-gray-100">
                    {visibleKeys.has(apiKey.id) ? apiKey.key : apiKey.masked_key}
                  </code>
                  
                  <button
                    onClick={() => toggleKeyVisibility(apiKey.id)}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                    title={visibleKeys.has(apiKey.id) ? 'Hide key' : 'Show key'}
                  >
                    {visibleKeys.has(apiKey.id) ? (
                      <EyeOff className="h-3 w-3" />
                    ) : (
                      <Eye className="h-3 w-3" />
                    )}
                  </button>
                  
                  <button
                    onClick={() => copyToClipboard(
                      visibleKeys.has(apiKey.id) ? apiKey.key : apiKey.masked_key, 
                      apiKey.id
                    )}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                    title="Copy to clipboard"
                  >
                    {copiedKey === apiKey.id ? (
                      <Check className="h-3 w-3 text-celadon-600 dark:text-celadon-400" />
                    ) : (
                      <Copy className="h-3 w-3" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
        <Link
          to="/api-keys"
          className="inline-flex items-center text-xs text-celadon-600 hover:text-celadon-700 dark:text-celadon-400 dark:hover:text-celadon-300 font-medium"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add API Key
        </Link>
      </div>
    </div>
  );
};

export default ApiKeysBlock; 